import React, { useEffect, useState } from 'react';
import { Card, InputNumber, Collapse, Empty, Tag, Spin, Space, Switch } from 'antd';
import { formatCurrency, formatSquareMeters } from 'utility/utility';
import styled from 'styled-components';
import {
  UnitDto,
  CreateUpdateDealUnitCommand,
  UnitFloorForDealDto,
  DealUnitDto,
  ProjectUnitsForDealDto,
  CategoryForDealDto,
  DealUnitFloorDto,
  DealPricingMode,
  DealBasicDetailsDto,
  DealPaymentsDto,
} from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { ProjectsApi } from 'api/api';

// Styled components remain the same...
const UnitsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  height: 100%;
  overflow-y: auto;
  max-height: 600px;
`;

const UnitsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const UnitCard = styled(Card)<{ $selected?: boolean; $isCurrentDeal?: boolean }>`
  flex: 1;
  min-width: 300px;
  max-width: calc(33.333% - 16px);
  transition: all 0.3s;
  border: 2px solid ${props => (props.$isCurrentDeal ? '#52c41a' : props.$selected ? '#1890ff' : '#d9d9d9')};
  background: ${props => (props.$isCurrentDeal ? '#f6ffed' : 'white')};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
  }

  @media (max-width: 992px) {
    max-width: calc(50% - 8px);
  }
`;

const UnitHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const UnitTitle = styled.div<{ $isCurrentDeal?: boolean }>`
  font-weight: 500;
  color: ${props => (props.$isCurrentDeal ? '#52c41a' : 'inherit')};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnitSubtitle = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

const FormGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const FormField = styled.div`
  flex: 1;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 4px;
`;

const TotalSection = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FloorAllocationWrapper = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const UnitAllocationWrapper = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
`;

const FloorAllocationRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FloorLabel = styled.div<{ $noStock?: boolean }>`
  min-width: 60px;
  font-size: 12px;
  ${props => props.$noStock && 'text-decoration: line-through; color: rgba(0, 0, 0, 0.45);'}
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .category-name {
    font-size: 13px;
    font-weight: 500;
  }
`;

const CurrentDealBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #52c41a;
  font-weight: 500;
`;

// Updated interface to work with split data structure
interface DealData {
  basic?: DealBasicDetailsDto;
  units?: DealUnitDto[];
  payments?: DealPaymentsDto;
}

interface Props {
  projectId?: number;
  value?: CreateUpdateDealUnitCommand[];
  onChange: (units: CreateUpdateDealUnitCommand[]) => void;
  defaultCollapsed?: boolean;
  editMode?: boolean;
  currentDealId?: number;
  deal?: DealData; // Updated to use the new split structure
  pricingMode?: DealPricingMode;
  onPricingModeChange?: (mode: DealPricingMode) => void;
}

const projectsApi = new ProjectsApi();

const UnitSelection: React.FC<Props> = ({
  projectId,
  value = [],
  onChange,
  defaultCollapsed,
  editMode = false,
  deal,
  pricingMode = DealPricingMode.NUMBER_0,
  onPricingModeChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [projectUnits, setProjectUnits] = useState<ProjectUnitsForDealDto | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (projectId) {
      fetchUnits();
    }
  }, [projectId]);

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await projectsApi.apiProjectsIdUnitsForDealGet({ id: projectId! });
      setProjectUnits(response.data);
    } catch (error) {
      console.error('Failed to fetch units:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editMode && deal?.units && value.length === 0) {
      const existingUnits = deal.units.map(dealUnit => ({
        unitId: dealUnit.unitId,
        name: dealUnit.unitName,
        negotiatedPricePerSqm: dealUnit.negotiatedPricePerSqm,
        totalPrice: dealUnit.totalPrice,
        squareMeters: dealUnit.squareMeters,
        quantity: dealUnit.quantity,
        floorAllocations: dealUnit.floors?.map(floor => ({
          unitFloorId: floor.unitFloorId,
          stock: floor.quantity,
        })),
      }));
      onChange(existingUnits);
    }
  }, [editMode, deal?.units, value.length, onChange]);

  // Helper function to check if a unit is part of the current deal
  const isUnitPartOfCurrentDeal = (unitId: number) => {
    return deal?.units?.some(dealUnit => dealUnit.unitId === unitId) || false;
  };

  // Helper function to sort units - deal units first, then others
  const sortUnitsWithDealFirst = (units: any[]) => {
    return [...units].sort((a, b) => {
      const aIsInDeal = isUnitPartOfCurrentDeal(a.id);
      const bIsInDeal = isUnitPartOfCurrentDeal(b.id);

      if (aIsInDeal && !bIsInDeal) return -1;
      if (!aIsInDeal && bIsInDeal) return 1;

      // If both are in deal or both are not in deal, sort by name
      return a.name.localeCompare(b.name);
    });
  };

  const calculateEffectiveStockOnUnitFloor = (
    unitFloor: UnitFloorForDealDto,
    unit: UnitDto,
    dealFloor?: DealUnitFloorDto,
  ) => {
    // Base stock and availability from category data
    const baseStock = unitFloor.stock || 0;
    const baseReserved = unitFloor.reserved || 0;
    const baseAvailable = unitFloor.available || 0;

    // Find any new allocations being made in the current session
    const newAllocation =
      value.find(u => u.unitId === unit.id)?.floorAllocations?.find(a => a.unitFloorId === unitFloor.id)?.stock || 0;

    const isUnitFloorPartOfCurrentDeal = editMode && dealFloor?.unitFloorId === unitFloor.id;
    const isNewAllocation = editMode && !isUnitFloorPartOfCurrentDeal && newAllocation > 0;

    if (editMode) {
      // In edit mode:
      // - baseAvailable includes the stock that was reserved by this deal
      // - We need to add back currentAllocation to available stock
      // - Then subtract any new allocation being made
      return {
        total: isUnitFloorPartOfCurrentDeal ? baseStock : isNewAllocation ? baseStock : baseAvailable,
        reserved: isUnitFloorPartOfCurrentDeal ? baseReserved : 0,
        available: baseAvailable,
        new: newAllocation === baseReserved ? 0 : newAllocation - baseReserved,
      };
    } else {
      // In create mode:
      // - baseAvailable is the true available stock
      // - Only need to consider new allocations being made
      return {
        total: baseAvailable,
        reserved: baseReserved + newAllocation,
        available: baseAvailable - newAllocation,
        new: newAllocation === baseReserved ? 0 : newAllocation,
      };
    }
  };

  const calculateEffectiveStockOnUnit = (unit: UnitDto) => {
    const category = projectUnits?.categories?.find(c => c.units?.some(u => u.id === unit.id));
    const originalDealUnit = deal?.units?.find(u => u.unitId === unit.id);

    if (!category?.hasFloors) {
      const effectiveStock = (unit.stock || 0) + (originalDealUnit?.quantity || 0);
      console.log(`unit-${unit.id}-effective-stock`, unit.stock, originalDealUnit?.quantity, effectiveStock);
      return effectiveStock;
    }

    return null;
  };

  const hasAvailableFloors = (unit: UnitDto) => {
    if (editMode) {
      return true; // In edit mode, show all units
    }
    return unit.floors?.some(floor => (floor.available || 0) > 0) || false;
  };

  const handleQuantityChangeOnUnit = (unit: UnitDto, quantity: number) => {
    const existingUnit = value.find(u => u.unitId === unit.id);
    const category = projectUnits?.categories?.find(c => c.units?.some(u => u.id === unit.id));

    if (!category?.hasFloors) {
      // Handle direct unit stock
      if (quantity <= 0 && existingUnit) {
        onChange(value.filter(u => u.unitId !== unit.id));
      } else {
        if (existingUnit) {
          onChange(
            value.map(u =>
              u.unitId === unit.id
                ? {
                    ...u,
                    name: unit.name,
                    quantity,
                    squareMeters: unit.squareMeters,
                    negotiatedPricePerSqm: u.negotiatedPricePerSqm,
                    totalPrice: u.totalPrice,
                  }
                : u,
            ),
          );
        } else if (quantity > 0) {
          onChange([
            ...value,
            {
              unitId: unit.id,
              name: unit.name,
              quantity,
              negotiatedPricePerSqm: 0,
              totalPrice: 0,
              squareMeters: unit.squareMeters,
              floorAllocations: [], // Empty for non-floor units
            },
          ]);
        }
      }
    }
  };

  const handleQuantityChangeOnUnitFloor = (unit: UnitDto, unitFloor: UnitFloorForDealDto, quantity: number) => {
    const existingUnit = value.find(u => u.unitId === unit.id);
    const newAllocations = [...(existingUnit?.floorAllocations || [])];
    const index = newAllocations.findIndex(a => a.unitFloorId === unitFloor.id);

    // Get the current allocation before any changes
    const currentAllocation = index >= 0 ? newAllocations[index].stock : 0;

    // Update allocations
    if (quantity <= 0 && index >= 0) {
      newAllocations.splice(index, 1);
    } else if (index >= 0) {
      newAllocations[index].stock = quantity;
    } else if (quantity > 0) {
      newAllocations.push({ unitFloorId: unitFloor.id, stock: quantity });
    }

    const totalQuantity = newAllocations.reduce((sum, a) => sum + (a.stock || 0), 0);

    // Update deal units data
    if (deal?.units) {
      const dealUnit = deal.units.find(u => u.unitId === unit.id);
      const dealFloor = dealUnit?.floors?.find(f => f.unitFloorId === unitFloor.id);

      // Calculate difference based on current allocation, not dealFloor
      const difference = quantity - (currentAllocation || 0);

      // Create new projectUnits with updated floor data
      setProjectUnits(prevUnits => {
        if (!prevUnits) return null;

        return {
          ...prevUnits,
          categories: prevUnits.categories?.map(category => ({
            ...category,
            units: category.units?.map(u => {
              if (u.id !== unit.id) return u;

              return {
                ...u,
                floors: u.floors?.map(f => {
                  if (f.id !== unitFloor.id) return f;

                  // Calculate new reserved and available values
                  const newReserved = Math.max(0, (f.reserved || 0) + difference);
                  const newAvailable = (f.stock || 0) - newReserved;

                  return {
                    ...f,
                    reserved: newReserved,
                    available: newAvailable,
                  };
                }),
              };
            }),
          })),
        };
      });

      if (dealUnit && dealFloor) {
        dealFloor.quantity = quantity;
      }
    }

    // Update form value
    if (existingUnit) {
      onChange(
        value.map(u =>
          u.unitId === unit.id
            ? { ...u, name: unit.name, floorAllocations: newAllocations, quantity: totalQuantity }
            : u,
        ),
      );
    } else {
      onChange([
        ...value,
        {
          unitId: unit.id,
          name: unit.name,
          negotiatedPricePerSqm: 0,
          totalPrice: 0,
          squareMeters: unit.squareMeters,
          floorAllocations: newAllocations,
          quantity: totalQuantity,
        },
      ]);
    }
  };

  const handlePriceChangeOnUnit = (unit: UnitDto, price: number) => {
    const existingIndex = value.findIndex(u => u.unitId === unit?.id);

    if (existingIndex >= 0) {
      // Update existing unit
      const newValue = [...value];
      newValue[existingIndex] = {
        ...newValue[existingIndex],
        name: unit.name,
        negotiatedPricePerSqm: pricingMode === DealPricingMode.NUMBER_0 ? price : 0,
        totalPrice: pricingMode === DealPricingMode.NUMBER_1 ? price : 0,
      };
      onChange(newValue);
    } else {
      // Add new unit
      onChange([
        ...value,
        {
          unitId: unit?.id!,
          name: unit.name,
          negotiatedPricePerSqm: pricingMode === DealPricingMode.NUMBER_0 ? price : 0,
          squareMeters: unit?.squareMeters!,
          totalPrice: pricingMode === DealPricingMode.NUMBER_1 ? price : 0,
          notes: '',
        },
      ]);
    }
  };

  const handlePriceChangeOnUnitfloor = (unit: UnitDto, price: number) => {
    const existingUnit = value.find(u => u.unitId === unit.id);
    const quantity = existingUnit?.floorAllocations?.reduce((sum, a) => sum + (a.stock || 0), 0) || 0;

    if (existingUnit) {
      onChange(
        value.map(u =>
          u.unitId === unit.id
            ? {
                ...u,
                name: unit.name,
                negotiatedPricePerSqm: pricingMode === DealPricingMode.NUMBER_0 ? price : 0,
                totalPrice: pricingMode === DealPricingMode.NUMBER_1 ? price : 0,
                quantity,
                floorAllocations: existingUnit.floorAllocations,
              }
            : u,
        ),
      );
    } else {
      onChange([
        ...value,
        {
          unitId: unit.id,
          name: unit.name,
          negotiatedPricePerSqm: pricingMode === DealPricingMode.NUMBER_0 ? price : 0,
          squareMeters: unit.squareMeters,
          totalPrice: pricingMode === DealPricingMode.NUMBER_1 ? price : 0,
          floorAllocations: [],
          quantity,
        },
      ]);
    }
  };

  const calculateCategoryStats = (category: CategoryForDealDto) => {
    if (category.hasFloors) {
      const available = category.floors?.reduce((sum, floor) => sum + (floor.availableUnitsCount || 0), 0) || 0;
      const totalReserved =
        category.units?.reduce(
          (sum, unit) => sum + (unit.floors?.reduce((floorSum, floor) => floorSum + (floor.reserved || 0), 0) || 0),
          0,
        ) || 0;
      const currentDealReserved =
        deal?.units
          ?.filter(u => category.units?.some(cu => cu.id === u.unitId))
          ?.reduce(
            (sum, unit) => sum + (unit.floors?.reduce((floorSum, floor) => floorSum + (floor.quantity || 0), 0) || 0),
            0,
          ) || 0;

      return {
        available,
        reserved: {
          total: totalReserved,
          current: editMode ? currentDealReserved : 0,
        },
      };
    } else {
      const available = category.units?.reduce((sum, unit) => sum + (unit.stock || 0), 0) || 0;
      const totalReserved = category.units?.reduce((sum, unit) => sum + (unit.reserved || 0), 0) || 0;
      const currentDealReserved =
        deal?.units
          ?.filter(u => category.units?.some(cu => cu.id === u.unitId))
          ?.reduce((sum, unit) => sum + (unit.quantity || 0), 0) || 0;

      return {
        available,
        reserved: {
          total: totalReserved,
          current: editMode ? currentDealReserved : 0,
        },
      };
    }
  };

  // Get active category keys based on deal units
  const getActiveCategoryKeys = () => {
    if (!editMode || !deal?.units?.length) return [];

    return (
      projectUnits?.categories
        ?.filter(category => deal?.units?.some(dealUnit => category.units?.some(unit => unit.id === dealUnit.unitId)))
        .map(category => category.id!.toString()) || []
    );
  };

  const getPriceLabel = () => {
    return pricingMode === DealPricingMode.NUMBER_0
      ? t('units:price-per-sqm', 'Price/m²')
      : t('units:total-price', 'Total Price');
  };

  const getPriceValue = (unit: UnitDto, selectedUnit?: CreateUpdateDealUnitCommand) => {
    if (pricingMode === DealPricingMode.NUMBER_0) {
      return selectedUnit?.negotiatedPricePerSqm || 0;
    }
    // Calculate total price
    return selectedUnit?.totalPrice || 0;
  };

  const handlePriceChange = (unit: UnitDto, price: number, selectedUnit?: CreateUpdateDealUnitCommand) => {
    if (unit.floors?.length) {
      handlePriceChangeOnUnitfloor(unit, price);
    } else {
      handlePriceChangeOnUnit(unit, price);
    }
  };

  if (!projectId) {
    return (
      <Empty
        image={<FeatherIcon icon="home" size={40} />}
        description={t('units:select-project-first', 'Please select a project first')}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin />
      </div>
    );
  }

  return (
    <UnitsContainer>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
        <Space>
          {t('deals:pricing-mode', 'Pricing Mode')}:
          <Switch
            checked={pricingMode === DealPricingMode.NUMBER_1}
            onChange={checked => onPricingModeChange?.(checked ? DealPricingMode.NUMBER_1 : DealPricingMode.NUMBER_0)}
            checkedChildren={t('deals:total-price', 'Total Price')}
            unCheckedChildren={t('deals:price-per-sqm', 'Price per m²')}
            disabled={editMode}
            style={{
              backgroundColor: pricingMode === DealPricingMode.NUMBER_1 ? '#52c41a' : '#1890ff',
            }}
          />
        </Space>
      </div>

      <Collapse
        defaultActiveKey={
          editMode
            ? getActiveCategoryKeys()
            : defaultCollapsed
            ? undefined
            : projectUnits?.categories?.map(c => c.id!.toString())
        }
      >
        {projectUnits?.categories?.map(category => {
          const stats = calculateCategoryStats(category);
          // Skip categories with no available units in create mode
          if (!editMode && stats.available === 0) {
            return null;
          }

          // Sort units to show deal units first
          const sortedUnits = category.units ? sortUnitsWithDealFirst(category.units) : [];

          return (
            <Collapse.Panel
              key={category.id!.toString()}
              header={
                <CategoryHeader>
                  <span className="category-name">{category.name}</span>
                  <Tag color="green">
                    {(() => {
                      return (
                        <>
                          {stats.available} {t('units:available', 'Available')}
                          {stats.reserved.total > 0 && (
                            <>
                              {' '}
                              · {stats.reserved.total} {t('units:reserved', 'Reserved')} ({stats.reserved.current}{' '}
                              {t('units:current', 'Current')})
                            </>
                          )}
                        </>
                      );
                    })()}
                  </Tag>
                </CategoryHeader>
              }
            >
              <UnitsGrid>
                {sortedUnits
                  .filter(unit => hasAvailableFloors(unit))
                  .map(unit => {
                    const selectedUnit = value.find(u => u.unitId === unit.id);
                    const effectiveStock = calculateEffectiveStockOnUnit(unit);
                    const isCurrentDealUnit = isUnitPartOfCurrentDeal(unit.id);
                    const totalPrice =
                      pricingMode === DealPricingMode.NUMBER_1
                        ? selectedUnit?.totalPrice || 0
                        : (selectedUnit?.quantity || 0) *
                          (selectedUnit?.negotiatedPricePerSqm || 0) *
                          unit.squareMeters!;

                    return (
                      <UnitCard
                        key={unit.id}
                        size="small"
                        $selected={!!selectedUnit}
                        $isCurrentDeal={isCurrentDealUnit}
                      >
                        <UnitHeader>
                          <div>
                            <UnitTitle $isCurrentDeal={isCurrentDealUnit}>{unit.name}</UnitTitle>
                            <UnitSubtitle>{formatSquareMeters(unit.squareMeters!)}</UnitSubtitle>
                          </div>
                          {effectiveStock !== null && (
                            <Tag color={effectiveStock > 0 ? 'green' : 'orange'}>
                              {effectiveStock} {t('units:available', 'Available')}
                              <br />
                              {editMode && selectedUnit?.quantity && (
                                <>
                                  {' '}
                                  ({selectedUnit.quantity} {t('units:current', 'Current')})
                                </>
                              )}
                            </Tag>
                          )}
                        </UnitHeader>

                        {!category.hasFloors ? (
                          <UnitAllocationWrapper>
                            <FormGroup>
                              <FormField>
                                <FieldLabel>{t('units:quantity', 'Quantity')}</FieldLabel>
                                <InputNumber
                                  style={{ width: '100%' }}
                                  size="small"
                                  min={0}
                                  max={effectiveStock!}
                                  value={selectedUnit?.quantity || 0}
                                  onChange={qty => handleQuantityChangeOnUnit(unit, qty || 0)}
                                />
                              </FormField>
                              <FormField>
                                <FieldLabel>{getPriceLabel()}</FieldLabel>
                                <InputNumber
                                  style={{ width: '100%' }}
                                  size="small"
                                  min={0}
                                  value={getPriceValue(unit, selectedUnit)}
                                  onChange={price => handlePriceChange(unit, price || 0, selectedUnit)}
                                  prefix="€"
                                />
                              </FormField>
                            </FormGroup>
                          </UnitAllocationWrapper>
                        ) : (
                          <>
                            <FloorAllocationWrapper>
                              {unit.floors?.map((floor: UnitFloorForDealDto) => {
                                const dealFloor = deal?.units
                                  ?.find(u => u.unitId === unit.id)
                                  ?.floors?.find(f => f.unitFloorId === floor.id);
                                const allocation = selectedUnit?.floorAllocations?.find(
                                  a => a.unitFloorId === floor.id,
                                );
                                const stock = calculateEffectiveStockOnUnitFloor(floor, unit, dealFloor);
                                const hasStock = stock.total > 0;

                                return (
                                  <FloorAllocationRow key={floor.id}>
                                    <FloorLabel $noStock={!hasStock}>
                                      {floor.description || `Floor ${floor.floorNumber}`}
                                    </FloorLabel>
                                    <InputNumber
                                      size="small"
                                      style={{ width: '100%' }}
                                      min={0}
                                      max={stock.total}
                                      value={dealFloor?.quantity || allocation?.stock || 0}
                                      onChange={qty => handleQuantityChangeOnUnitFloor(unit, floor, qty || 0)}
                                      disabled={!hasStock}
                                    />
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div>
                                        <Tag
                                          color={stock.available > 0 ? 'green' : 'red'}
                                          style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            padding: '4px 8px',
                                            lineHeight: '1.5',
                                          }}
                                        >
                                          <span>
                                            {stock.available} {t('units:available', 'Available')}
                                          </span>
                                        </Tag>
                                      </div>
                                      {editMode && (stock.reserved > 0 || stock.new > 0) && (
                                        <div>
                                          <Tag
                                            color="orange"
                                            style={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              padding: '4px 8px',
                                              lineHeight: '1.5',
                                            }}
                                          >
                                            <span style={{ display: 'flex', flexDirection: 'column' }}>
                                              <span>
                                                {stock.reserved} {t('units:reserved', 'Reserved')}
                                              </span>
                                              {stock.new > 0 && (
                                                <span style={{ fontSize: '12px' }}>
                                                  (+{stock.new} {t('units:new', 'new')})
                                                </span>
                                              )}
                                            </span>
                                          </Tag>
                                        </div>
                                      )}
                                    </div>
                                  </FloorAllocationRow>
                                );
                              })}
                            </FloorAllocationWrapper>
                            <FormGroup style={{ marginTop: '12px' }}>
                              <FormField>
                                <FieldLabel>{getPriceLabel()}</FieldLabel>
                                <InputNumber
                                  style={{ width: '100%' }}
                                  size="small"
                                  min={0}
                                  value={getPriceValue(unit, selectedUnit)}
                                  onChange={price => handlePriceChange(unit, price || 0, selectedUnit)}
                                  prefix="€"
                                />
                              </FormField>
                            </FormGroup>
                          </>
                        )}

                        {selectedUnit && (
                          <TotalSection>
                            <TotalRow>
                              <span>{t('units:total', 'Total')}:</span>
                              <span>{formatCurrency(totalPrice)}</span>
                            </TotalRow>
                          </TotalSection>
                        )}
                      </UnitCard>
                    );
                  })}
              </UnitsGrid>
            </Collapse.Panel>
          );
        })}
      </Collapse>
    </UnitsContainer>
  );
};

export default UnitSelection;
